import { PageTabs } from "constants/ui"

// Helper to get menu name from page key
export const getMenuNameFromPageKey = (pageKey) => {
    const menuMapping = {
        'MERCHANT_DETAIL': 'Merchants',
        'MERCHANT_GROUP_DETAIL': 'Merchant Groups',
        'BRAND_DETAIL': 'Brands',
        'CURATOR_OFFERS_DETAIL': 'Curator Offers',
        'OPERATION_OFFERS_DETAIL': 'Operation Offers',
        'APPROVER_OFFERS_DETAIL': 'Approver Offers',
        'PUBLISHED_OFFERS_DETAIL': 'Published Offers',
        'USER_DETAIL': 'Users'
    }
    return menuMapping[pageKey]
}

// function to match backend tabs with frontend configuration
const getMatchedTabs = (pageKey, userMenu = []) => {
    const frontendTabs = PageTabs[pageKey]?.tabs || []
    if (frontendTabs.length === 0) return []

    const menuName = getMenuNameFromPageKey(pageKey)
    if (!menuName) return frontendTabs
    
    const menuItem = userMenu.find(menu => menu.menu === menuName)
    if (!menuItem || !menuItem.menuTabs) return frontendTabs

    // Filter backend tabs that match frontend tab names
    const matchedTabs = menuItem.menuTabs
        .sort((a, b) => parseInt(a.displayOrder) - parseInt(b.displayOrder))
        .map(userMenuTab => {
            const frontendTab = frontendTabs.find(frontendTab => 
                frontendTab.name.toLowerCase() === userMenuTab.tabName.toLowerCase()
            )
            return {
                ...frontendTab,
                ...userMenuTab,
                name: userMenuTab.tabName,
                frontendConfig: frontendTab
            }
        })

    return matchedTabs
}

export const getTabNames = (pageKey, userMenu = []) => {
    // If no userMenu provided
    if (!userMenu || userMenu.length === 0) {
        return PageTabs[pageKey]?.tabs?.map(tab => tab.name) || []
    }
    
    // Use matched tabs logic
    const matchedTabs = getMatchedTabs(pageKey, userMenu)
    return matchedTabs.map(tab => tab.name)
}

export const getTabPermissions = (pageKey, mode = "detail", userMenu = []) => {
    // If no userMenu provided
    if (!userMenu || userMenu.length === 0) {
        const pageConfig = PageTabs[pageKey]
        if (!pageConfig?.tabs) return []
        
        const actionKey = mode === "detail" ? "get" : mode
        return pageConfig.tabs.map(tab => tab[actionKey] || {})
    }
    
    // Use matched tabs logic
    const matchedTabs = getMatchedTabs(pageKey, userMenu)
    const actionKey = mode === "detail" ? "get" : mode
    return matchedTabs.map(tab => tab.frontendConfig?.[actionKey] || {})
}

export const isTabMandatory = (pageKey, tabName, userMenu = []) => {
    if (!userMenu || userMenu.length === 0) return false
    
    const matchedTabs = getMatchedTabs(pageKey, userMenu)
    const tab = matchedTabs.find(tab => 
        tab.name.toLowerCase() === tabName.toLowerCase()
    )
    return tab?.isMandatory === "true"
}

// Get all backend tabs with frontend config
export const getBackendTabs = (pageKey, userMenu = []) => {
    return getMatchedTabs(pageKey, userMenu)
}

// Get the first tab permission (displayOrder = 1) for View button permission check
export const getFirstTabPermission = (pageKey, userMenu = []) => {
    const frontendTabs = PageTabs[pageKey]?.tabs || []
    if (frontendTabs.length === 0) return null

    const menuName = getMenuNameFromPageKey(pageKey)
    if (!menuName) {
        // Fallback to first frontend tab
        return frontendTabs[0]?.get || null
    }

    const menuItem = userMenu.find(menu => menu.menu === menuName)
    if (!menuItem || !menuItem.menuTabs || menuItem.menuTabs.length === 0) {
        // Fallback to first frontend tab
        return frontendTabs[0]?.get || null
    }

    // Find the tab with displayOrder = "1"
    const firstOrderTab = menuItem.menuTabs.find(tab => tab.displayOrder === "1")
    if (!firstOrderTab) {
        // Fallback to first frontend tab
        return frontendTabs[0]?.get || null
    }

    // Match with frontend config to get the permission details
    const matchedFrontendTab = frontendTabs.find(frontendTab =>
        frontendTab.name.toLowerCase() === firstOrderTab.tabName.toLowerCase()
    )

    return matchedFrontendTab?.get || null
}